import { jsPDF } from 'jspdf';
import api from '../../api';

const BLUE = [18, 62, 115];
const TEXT = [24, 50, 79];
const MUTED = [102, 123, 145];
const LIGHT = [238, 245, 252];
const BORDER = [215, 226, 239];

const text = (value) => String(value ?? '').trim();
const compact = (items) => items.filter(Boolean);

const formatTaxId = (value) => {
  const digits = text(value).replace(/\D/g, '').slice(0, 14);
  return digits.length === 14
    ? digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')
    : digits;
};

const generatedAt = () => new Intl.DateTimeFormat('pt-BR', {
  dateStyle: 'short',
  timeStyle: 'short',
}).format(new Date());

const blobToDataUrl = (blob) => new Promise((resolve) => {
  if (!blob || typeof FileReader === 'undefined') {
    resolve(null);
    return;
  }
  const reader = new FileReader();
  reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
  reader.onerror = () => resolve(null);
  reader.readAsDataURL(blob);
});

const imageDataFromUrl = async (url) => {
  if (!url) return null;
  try {
    const response = await fetch(url, { credentials: 'include' });
    if (!response.ok) return null;
    return await blobToDataUrl(await response.blob());
  } catch {
    return null;
  }
};

const organizationLogoData = async (organization) => {
  const embedded = text(organization?.logoDataUrl);
  if (/^data:image\/(png|jpe?g);base64,/i.test(embedded)) return embedded;
  if (!text(organization?.logoUrl)) return null;

  // A logo institucional é protegida no iBPM. Usar a mesma rota autenticada
  // dos demais relatórios evita que um fetch direto do logoUrl falhe por token.
  try {
    const response = await api.get('/company/logo/content', { responseType: 'blob' });
    const authenticated = await blobToDataUrl(response?.data);
    if (authenticated) return authenticated;
  } catch {
    // Mantém compatibilidade com instalações antigas onde logoUrl é público.
  }
  return imageDataFromUrl(organization.logoUrl);
};

const organizationData = (organization) => {
  const name = text(organization?.tradeName) || text(organization?.name) || 'Organização';
  const taxId = formatTaxId(organization?.taxId);
  return { name, taxId };
};

const safeFilename = (value) => text(value)
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-zA-Z0-9._-]+/g, '-')
  .replace(/-+/g, '-')
  .replace(/^-|-$/g, '') || 'relatorio.pdf';

export async function downloadYoungApprenticeTablePdf({
  title,
  subtitle = '',
  organization,
  rows = [],
  columns = [],
  filters = '',
  filename = 'relatorio.pdf',
}) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 10;
  const tableWidth = pageWidth - margin * 2;
  const footerY = pageHeight - 8;
  const organizationInfo = organizationData(organization);
  const logoData = await organizationLogoData(organization);
  const createdAt = generatedAt();
  const weights = columns.map((column) => Math.max(0.5, Number(column.weight || 1)));
  const totalWeight = weights.reduce((sum, value) => sum + value, 0) || 1;
  const widths = weights.map((weight) => tableWidth * weight / totalWeight);

  const drawBrandHeader = () => {
    doc.setFillColor(...BLUE);
    doc.rect(0, 0, pageWidth, 35, 'F');

    if (logoData) {
      try {
        const format = /^data:image\/png/i.test(logoData) ? 'PNG' : 'JPEG';
        doc.addImage(logoData, format, margin, 6.4, 22.5, 22);
      } catch {
        doc.setFillColor(255, 255, 255);
        doc.circle(margin + 10.5, 17.5, 8.5, 'F');
        doc.setTextColor(...BLUE);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7);
        doc.text('NASH', margin + 10.5, 19, { align: 'center' });
      }
    } else {
      doc.setFillColor(255, 255, 255);
      doc.circle(margin + 10.5, 17.5, 8.5, 'F');
      doc.setTextColor(...BLUE);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.text('NASH', margin + 10.5, 19, { align: 'center' });
    }

    const titleX = margin + 29;
    const titleWidth = pageWidth - titleX - margin - 45;
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.text(text(title).toUpperCase(), titleX, 10.5, { maxWidth: titleWidth });
    if (subtitle) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      doc.text(text(subtitle), titleX, 15.2, { maxWidth: titleWidth });
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.7);
    doc.text(organizationInfo.name, titleX, 21.2, { maxWidth: titleWidth });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.4);
    if (organizationInfo.taxId) doc.text(`CNPJ ${organizationInfo.taxId}`, titleX, 26.2, { maxWidth: titleWidth });

    doc.setFontSize(6.6);
    doc.text(`Gerado em ${createdAt}`, pageWidth - margin, 10.5, { align: 'right' });
    doc.text(`${rows.length} registro(s)`, pageWidth - margin, 15.2, { align: 'right' });
  };

  const drawFilterSummary = () => {
    doc.setFillColor(...LIGHT);
    doc.setDrawColor(...BORDER);
    doc.roundedRect(margin, 39, tableWidth, 10, 2, 2, 'FD');
    doc.setTextColor(...TEXT);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.8);
    doc.text('FILTROS', margin + 3, 43.2);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...MUTED);
    const summary = text(filters) || 'Sem filtros adicionais';
    doc.text(summary, margin + 18, 43.2, { maxWidth: tableWidth - 22 });
    doc.setTextColor(...TEXT);
    doc.setFont('helvetica', 'bold');
    doc.text(`${columns.length} coluna(s)`, pageWidth - margin - 3, 46.6, { align: 'right' });
  };

  const drawTableHeader = (y) => {
    doc.setFillColor(...BLUE);
    doc.setDrawColor(...BLUE);
    doc.rect(margin, y, tableWidth, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(Math.max(5.3, Math.min(7, 10 - columns.length * 0.18)));
    let x = margin;
    columns.forEach((column, index) => {
      doc.text(text(column.label), x + 1.8, y + 5, { maxWidth: widths[index] - 3.6 });
      x += widths[index];
    });
    return y + 8;
  };

  const drawPageStart = () => {
    drawBrandHeader();
    drawFilterSummary();
    return drawTableHeader(52);
  };

  let y = drawPageStart();
  const fontSize = Math.max(5.1, Math.min(6.8, 9.2 - columns.length * 0.16));
  doc.setFontSize(fontSize);

  for (let rowIndex = 0; rowIndex < rows.length; rowIndex += 1) {
    const row = rows[rowIndex];
    const cellLines = columns.map((column, index) => {
      const raw = column.value ? column.value(row) : row?.[column.key];
      const value = text(raw) || '—';
      return doc.splitTextToSize(value, Math.max(8, widths[index] - 3.6));
    });
    const maxLines = Math.max(1, ...cellLines.map((lines) => lines.length));
    const rowHeight = Math.max(7, maxLines * 3.05 + 3.2);
    if (y + rowHeight > footerY - 6) {
      doc.addPage('a4', 'landscape');
      y = drawPageStart();
      doc.setFontSize(fontSize);
    }

    if (rowIndex % 2 === 1) {
      doc.setFillColor(248, 251, 254);
      doc.rect(margin, y, tableWidth, rowHeight, 'F');
    }
    doc.setDrawColor(...BORDER);
    doc.line(margin, y + rowHeight, margin + tableWidth, y + rowHeight);
    doc.setTextColor(...TEXT);
    doc.setFont('helvetica', 'normal');
    let x = margin;
    cellLines.forEach((lines, index) => {
      doc.text(lines, x + 1.8, y + 4.2);
      x += widths[index];
      if (index < cellLines.length - 1) doc.line(x, y, x, y + rowHeight);
    });
    y += rowHeight;
  }

  if (!rows.length) {
    doc.setTextColor(...MUTED);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text('Nenhum registro encontrado para os filtros informados.', margin + 3, y + 8);
  }

  const totalPages = doc.getNumberOfPages();
  for (let page = 1; page <= totalPages; page += 1) {
    doc.setPage(page);
    doc.setDrawColor(...BORDER);
    doc.line(margin, footerY - 3.2, pageWidth - margin, footerY - 3.2);
    doc.setTextColor(...MUTED);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.4);
    doc.text('Relatório gerado pelo sistema Intelligent BPM Nash Tecnologia. Site: www.nashtec.com.br', pageWidth / 2, footerY, { align: 'center' });
    doc.text(`Página ${page} de ${totalPages}`, pageWidth - margin, footerY, { align: 'right' });
  }

  const target = safeFilename(filename.toLowerCase().endsWith('.pdf') ? filename : `${filename}.pdf`);
  doc.save(target);
}
